/**
 * Age Verifier Module for AppBouncer SDK
 *
 * Provides threshold-based age verification using an ONNX model that outputs
 * probabilities for age thresholds (12+, 15+, 18+, 21+, 25+).
 *
 * Key differences from the bucket-based AgeEstimator:
 * - Outputs P(age >= threshold) for each threshold
 * - Uses ImageNet normalization (mean/std)
 * - Input size is configurable (224×224 for EfficientNet-Lite0, 260×260 for legacy)
 * - Better suited for age verification vs. age estimation
 *
 * Model Configuration:
 * The model path and input size are read from centralized SDK configuration.
 * To switch models, use setGlobalConfig() or pass config to the constructor.
 *
 * @see config/index.ts for AgeVerifierConfig
 * @see constants/index.ts for AGE_VERIFIER_MODELS presets
 */

import * as ort from 'onnxruntime-web/wasm';
import { getAgeVerifierConfig, type AgeVerifierConfig } from '../config';
import { AGE_VERIFIER } from '../constants';

// ============================================================
// Types
// ============================================================

/** Age thresholds supported by the model (re-exported from constants) */
export const AGE_THRESHOLDS = AGE_VERIFIER.THRESHOLDS;
export type AgeThreshold = (typeof AGE_THRESHOLDS)[number];

/**
 * Local configuration for the Age Verifier.
 * Extends the centralized AgeVerifierConfig with runtime overrides.
 */
export interface LocalAgeVerifierConfig extends AgeVerifierConfig {
  /** Risk tolerance as a field name matching legacy API */
  defaultRiskTolerance: number;
}

/**
 * Result from threshold-based age verification
 */
export interface AgeVerificationResult {
  /** Whether the person is verified as above the minimum age */
  verified: boolean;
  /** Probability that the person is at or above the minimum age (0-1) */
  confidence: number;
  /** Probability that the person is below the minimum age (0-1) */
  probUnderage: number;
  /** The minimum age threshold used for verification */
  minAge: AgeThreshold;
  /** Probabilities for all thresholds */
  allProbabilities: Record<string, number>;
  /** Whether this is a demo/fallback result */
  isDemoResult?: boolean;
}

/**
 * Result with captured image for multi-estimate
 */
export interface AgeVerificationWithImage {
  result: AgeVerificationResult;
  imageDataUrl: string | null;
}

/**
 * Configuration for multi-estimate verification
 */
export interface MultiVerifyConfig {
  /** Number of verification runs (default: 8) */
  numEstimates?: number;
  /** Delay between estimates in ms (default: 500) */
  delayBetweenMs?: number;
  /** Minimum confidence to include result (default: 0.5) */
  confidenceThreshold?: number;
  /** Progress callback */
  onProgress?: (current: number, total: number) => void;
  /** Capture images for each estimate (default: true) */
  captureImages?: boolean;
}

/**
 * Configuration for live multi-estimate verification from video
 */
export interface LiveMultiVerifyConfig extends MultiVerifyConfig {
  /** Countdown seconds before starting captures (default: 3) */
  countdownSeconds?: number;
  /** Callback for countdown updates */
  onCountdown?: (secondsRemaining: number) => void;
  /** Callback to get fresh landmarks for each capture */
  getLandmarks: () => Array<{ x: number; y: number }> | null;
}

/**
 * Aggregated result from multiple verification runs
 */
export interface MultiVerificationResult extends AgeVerificationResult {
  /** Best image (closest to average confidence) */
  bestImage: string | null;
  /** All individual estimates */
  allEstimates?: AgeVerificationWithImage[];
  /** Number of valid estimates used */
  estimatesUsed: number;
  /** Number of individual verifications that passed (for pass-rate mode) */
  passedCount?: number;
  /** Pass rate percentage (passedCount / estimatesUsed * 100) */
  passRate?: number;
  /** True when failure was caused by no face being detected in captures */
  noFaceDetected?: boolean;
}

// ============================================================
// Default Configuration
// ============================================================

/**
 * Build the default verifier config from centralized SDK configuration.
 * This function is called at runtime to get the current config.
 */
function buildDefaultConfig(): LocalAgeVerifierConfig {
  const globalConfig = getAgeVerifierConfig();
  return {
    ...globalConfig,
    defaultRiskTolerance: globalConfig.riskTolerance,
  };
}

/**
 * @deprecated Use getAgeVerifierConfig() instead. Kept for backward compatibility.
 */
export const DEFAULT_VERIFIER_CONFIG: LocalAgeVerifierConfig = buildDefaultConfig();

// ============================================================
// Constants from centralized configuration
// ============================================================

// ImageNet normalization constants (fixed values from ImageNet dataset)
const IMAGENET_MEAN = AGE_VERIFIER.IMAGENET_NORMALIZATION.MEAN;
const IMAGENET_STD = AGE_VERIFIER.IMAGENET_NORMALIZATION.STD;

// Face crop size - intermediate size before resize to model input
const FACE_CROP_SIZE = AGE_VERIFIER.FACE_ALIGNMENT.CROP_SIZE;

// Eye distance ratio - what percentage of output width the eye distance should be
const EYE_DISTANCE_RATIO = AGE_VERIFIER.FACE_ALIGNMENT.EYE_DISTANCE_RATIO;

// MediaPipe landmark indices for eye centers
const LEFT_EYE_INDICES = AGE_VERIFIER.LANDMARK_INDICES.LEFT_EYE;
const RIGHT_EYE_INDICES = AGE_VERIFIER.LANDMARK_INDICES.RIGHT_EYE;

// ============================================================
// Age Verifier Class
// ============================================================

/**
 * AgeVerifier provides threshold-based age verification.
 *
 * Unlike the bucket-based AgeEstimator that gives an estimated age,
 * AgeVerifier directly answers: "Is this person likely X+ years old?"
 * with a confidence score for each threshold.
 *
 * @example
 * ```typescript
 * // Use default configuration (from SDK global config)
 * const verifier = new AgeVerifier();
 *
 * // Override with custom config
 * const verifier = new AgeVerifier({
 *   modelPath: './models/18_int8.onnx',  // Use INT8 model for mobile
 *   inputSize: 224,
 * });
 * ```
 */
export class AgeVerifier {
  private config: LocalAgeVerifierConfig;
  private session: ort.InferenceSession | null = null;
  private isInitialized = false;

  constructor(config: Partial<LocalAgeVerifierConfig> = {}) {
    // Build config from centralized SDK config, then override with constructor params
    const defaultConfig = buildDefaultConfig();
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Check if verifier is in demo mode.
   * @deprecated Always returns false — demo mode has been removed for security.
   */
  isInDemoMode(): boolean {
    return false;
  }

  /**
   * Get current execution provider
   */
  getExecutionProvider(): string {
    return this.config.executionProvider;
  }

  /**
   * Initialize the ONNX model.
   *
   * The model path and input size are read from configuration.
   * If model loading fails, falls back to demo mode.
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Configure WASM files to load from CDN
      ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/';
      ort.env.wasm.numThreads = 1;

      const modelPath = this.config.modelPath;
      const inputSize = this.config.inputSize;
      console.log(`[AgeVerifier] Loading model: ${modelPath} (input: ${inputSize}×${inputSize})`);

      const response = await fetch(modelPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch model: ${response.status}`);
      }

      const modelBuffer = await response.arrayBuffer();
      console.log(
        `[AgeVerifier] Model size: ${(modelBuffer.byteLength / 1024 / 1024).toFixed(1)} MB`
      );

      this.session = await ort.InferenceSession.create(modelBuffer, {
        executionProviders: [this.config.executionProvider],
      });

      this.isInitialized = true;
      console.log('[AgeVerifier] Model loaded successfully');
    } catch (error) {
      console.error('[AgeVerifier] Model failed to load:', error);
      throw new Error(
        `Age verification model failed to load: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Preprocess image for the model using ImageNet normalization.
   * Converts RGBA ImageData to CHW float32 tensor.
   */
  private preprocessImage(imageData: ImageData): Float32Array {
    const { data, width, height } = imageData;
    const inputSize = this.config.inputSize;

    // Resize if needed
    let pixelData = data;
    if (width !== inputSize || height !== inputSize) {
      const canvas = document.createElement('canvas');
      canvas.width = inputSize;
      canvas.height = inputSize;
      const ctx = canvas.getContext('2d')!;

      // Create temporary canvas with original data
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d')!;
      tempCtx.putImageData(imageData, 0, 0);

      // Draw scaled
      ctx.drawImage(tempCanvas, 0, 0, inputSize, inputSize);
      pixelData = ctx.getImageData(0, 0, inputSize, inputSize).data;
    }

    // Convert to CHW format with ImageNet normalization
    // Note: The Python model expects RGB order and CHW format
    const chw = new Float32Array(3 * inputSize * inputSize);
    const pixelCount = inputSize * inputSize;

    // Red channel (normalized with ImageNet mean/std)
    for (let i = 0; i < pixelCount; i++) {
      const r = pixelData[i * 4] / 255.0;
      chw[i] = (r - IMAGENET_MEAN[0]) / IMAGENET_STD[0];
    }

    // Green channel
    for (let i = 0; i < pixelCount; i++) {
      const g = pixelData[i * 4 + 1] / 255.0;
      chw[pixelCount + i] = (g - IMAGENET_MEAN[1]) / IMAGENET_STD[1];
    }

    // Blue channel
    for (let i = 0; i < pixelCount; i++) {
      const b = pixelData[i * 4 + 2] / 255.0;
      chw[2 * pixelCount + i] = (b - IMAGENET_MEAN[2]) / IMAGENET_STD[2];
    }

    return chw;
  }

  /**
   * Extract eye center from landmark indices.
   * Returns pixel coordinates.
   */
  private getEyeCenter(
    landmarks: Array<{ x: number; y: number }>,
    indices: readonly number[],
    width: number,
    height: number
  ): { x: number; y: number } {
    let sumX = 0;
    let sumY = 0;
    let count = 0;

    for (const idx of indices) {
      if (idx < landmarks.length) {
        sumX += landmarks[idx].x * width;
        sumY += landmarks[idx].y * height;
        count++;
      }
    }

    return count > 0 ? { x: sumX / count, y: sumY / count } : { x: width / 2, y: height / 2 };
  }

  /**
   * Align face based on eye positions (dataset-style preprocessing).
   *
   * This matches the Python infer.py preprocessing:
   * 1. Rotates so eyes are horizontal
   * 2. Scales so inter-eye distance is consistent (40% of output size)
   * 3. Positions eyes at 35% from top of output
   * 4. Outputs 200x200 aligned face crop
   */
  private alignFace(
    source: HTMLVideoElement | HTMLCanvasElement,
    leftEye: { x: number; y: number },
    rightEye: { x: number; y: number },
    outputSize: number = FACE_CROP_SIZE
  ): HTMLCanvasElement {
    // Calculate angle between eyes
    const dx = rightEye.x - leftEye.x;
    const dy = rightEye.y - leftEye.y;
    const angle = Math.atan2(dy, dx); // radians

    // Calculate inter-eye distance
    const eyeDistance = Math.sqrt(dx * dx + dy * dy);

    // Target eye distance as percentage of output size
    // Lower ratio = more face visible, higher = tighter crop
    const targetEyeDistance = outputSize * EYE_DISTANCE_RATIO;
    const scale = targetEyeDistance / eyeDistance;

    // Eye center
    const eyeCenterX = (leftEye.x + rightEye.x) / 2;
    const eyeCenterY = (leftEye.y + rightEye.y) / 2;

    // Target position: eyes at 35% from top, centered horizontally
    const targetEyeX = outputSize / 2;
    const targetEyeY = outputSize * 0.35;

    // Create output canvas
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d')!;

    // Use high-quality interpolation (closer to cv2.INTER_LINEAR)
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Apply transformation:
    // 1. Translate to put eye center at origin
    // 2. Rotate to make eyes horizontal
    // 3. Scale to consistent size
    // 4. Translate to target position
    ctx.translate(targetEyeX, targetEyeY);
    ctx.rotate(-angle); // negative because we want to counter-rotate
    ctx.scale(scale, scale);
    ctx.translate(-eyeCenterX, -eyeCenterY);

    // Draw the source image with transformation applied
    ctx.drawImage(source, 0, 0);

    return canvas;
  }

  /**
   * Crop face tightly from video/canvas.
   *
   * 1. Get face bounding box from landmarks
   * 2. Tight crop (minimal padding)
   * 3. Resize to model input size (configured, default 224×224)
   */
  cropFaceRegion(
    source: HTMLVideoElement | HTMLCanvasElement,
    landmarks: Array<{ x: number; y: number }>,
    _padding = 0
  ): HTMLCanvasElement {
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

    // Get face bounding box from landmarks
    const xs = landmarks.map((p) => p.x * sourceWidth);
    const ys = landmarks.map((p) => p.y * sourceHeight);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Center and size - tight crop (just 5% padding)
    const w = maxX - minX;
    const h = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const size = Math.max(w, h) * 1.05; // 5% padding only

    // Crop bounds
    let x1 = cx - size / 2;
    let y1 = cy - size / 2;
    let x2 = cx + size / 2;
    let y2 = cy + size / 2;

    // Clamp
    x1 = Math.max(0, x1);
    y1 = Math.max(0, y1);
    x2 = Math.min(sourceWidth, x2);
    y2 = Math.min(sourceHeight, y2);

    // Output: 260x260
    const canvas = document.createElement('canvas');
    canvas.width = this.config.inputSize;
    canvas.height = this.config.inputSize;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      source,
      x1,
      y1,
      x2 - x1,
      y2 - y1,
      0,
      0,
      this.config.inputSize,
      this.config.inputSize
    );

    return canvas;
  }

  /**
   * Crop face with eye-based alignment (dataset-style preprocessing).
   *
   * This is kept for cases where precise alignment is needed.
   * Uses the same approach as Python's infer.py with alignment.
   */
  cropFaceAligned(
    source: HTMLVideoElement | HTMLCanvasElement,
    landmarks: Array<{ x: number; y: number }>
  ): HTMLCanvasElement {
    // Get source dimensions
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

    // Extract eye centers from MediaPipe landmarks
    const leftEye = this.getEyeCenter(landmarks, LEFT_EYE_INDICES, sourceWidth, sourceHeight);
    const rightEye = this.getEyeCenter(landmarks, RIGHT_EYE_INDICES, sourceWidth, sourceHeight);

    // Validate eye detection
    const eyeDistance = Math.sqrt(
      Math.pow(rightEye.x - leftEye.x, 2) + Math.pow(rightEye.y - leftEye.y, 2)
    );

    // If eyes are too close (invalid detection), fall back to simple crop
    if (eyeDistance < 10) {
      console.warn('[AgeVerifier] Invalid eye detection, falling back to simple crop');
      return this.cropFaceSimple(source, landmarks);
    }

    // Align face to 200x200 using eye positions
    const alignedFace = this.alignFace(source, leftEye, rightEye, FACE_CROP_SIZE);

    // If model expects different size, resize the aligned face
    if (this.config.inputSize !== FACE_CROP_SIZE) {
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = this.config.inputSize;
      resizedCanvas.height = this.config.inputSize;
      const ctx = resizedCanvas.getContext('2d')!;
      // Use high-quality interpolation (closer to cv2.INTER_LINEAR)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(alignedFace, 0, 0, this.config.inputSize, this.config.inputSize);
      return resizedCanvas;
    }

    return alignedFace;
  }

  /**
   * Fallback simple face crop (bounding box based).
   * Used when eye detection fails.
   */
  private cropFaceSimple(
    source: HTMLVideoElement | HTMLCanvasElement,
    landmarks: Array<{ x: number; y: number }>
  ): HTMLCanvasElement {
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

    // Calculate bounding box from landmarks
    const xs = landmarks.map((p) => p.x * sourceWidth);
    const ys = landmarks.map((p) => p.y * sourceHeight);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Calculate center and size with 30% padding (matching Python fallback)
    const w = maxX - minX;
    const h = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const size = Math.max(w, h) * 1.3;

    // Clamp to source bounds
    let x1 = cx - size / 2;
    let y1 = cy - size / 2;
    let x2 = cx + size / 2;
    let y2 = cy + size / 2;

    // Handle boundary clipping
    x1 = Math.max(0, x1);
    y1 = Math.max(0, y1);
    x2 = Math.min(sourceWidth, x2);
    y2 = Math.min(sourceHeight, y2);

    const cropW = x2 - x1;
    const cropH = y2 - y1;

    // Create output canvas at model input size
    const canvas = document.createElement('canvas');
    canvas.width = this.config.inputSize;
    canvas.height = this.config.inputSize;
    const ctx = canvas.getContext('2d')!;

    // Use high-quality interpolation (closer to cv2.INTER_LINEAR)
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw cropped and scaled face directly to model input size
    ctx.drawImage(source, x1, y1, cropW, cropH, 0, 0, this.config.inputSize, this.config.inputSize);

    return canvas;
  }

  /**
   * Verify age from cropped face canvas
   */
  async verify(
    faceCanvas: HTMLCanvasElement,
    minAge: AgeThreshold = 18,
    riskTolerance: number = this.config.defaultRiskTolerance
  ): Promise<AgeVerificationResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Validate minAge
    if (!AGE_THRESHOLDS.includes(minAge)) {
      throw new Error(`minAge must be one of ${AGE_THRESHOLDS.join(', ')}`);
    }

    // No ONNX session means the model didn't load — cannot verify
    if (!this.session) {
      throw new Error(
        '[AgeVerifier] Cannot verify: ONNX model session not available. Call initialize() first.'
      );
    }

    const inputSize = this.config.inputSize;
    const ctx = faceCanvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, faceCanvas.width, faceCanvas.height);

    // Preprocess
    const inputTensor = this.preprocessImage(imageData);
    const input = new ort.Tensor('float32', inputTensor, [1, 3, inputSize, inputSize]);

    // Debug: Log tensor stats for comparison with Python
    if (this.config.debug) {
      // Calculate stats efficiently (avoid spread on large arrays)
      let min = inputTensor[0],
        max = inputTensor[0],
        sum = 0;
      for (let i = 0; i < inputTensor.length; i++) {
        if (inputTensor[i] < min) min = inputTensor[i];
        if (inputTensor[i] > max) max = inputTensor[i];
        sum += inputTensor[i];
      }
      console.log('[AgeVerifier] Tensor stats (compare with Python):');
      console.log('  Shape:', [1, 3, inputSize, inputSize]);
      console.log('  Min:', min.toFixed(6));
      console.log('  Max:', max.toFixed(6));
      console.log('  Mean:', (sum / inputTensor.length).toFixed(6));
      console.log(
        '  First 5 R:',
        Array.from(inputTensor.slice(0, 5)).map((v) => v.toFixed(6))
      );
      console.log('  Canvas size:', faceCanvas.width, 'x', faceCanvas.height);
      console.log('  ImageData size:', imageData.width, 'x', imageData.height);
    }

    // Run inference using configurable input/output names
    const inputName = this.config.inputName;
    const outputName = this.config.outputName;
    const results = await this.session.run({ [inputName]: input });
    const probs = results[outputName].data as Float32Array;

    // Build probabilities map
    const allProbabilities: Record<string, number> = {};
    AGE_THRESHOLDS.forEach((threshold, idx) => {
      allProbabilities[`${threshold}+`] = probs[idx];
    });

    // Get probability for the requested minimum age
    const thresholdIdx = AGE_THRESHOLDS.indexOf(minAge);
    const probAdult = probs[thresholdIdx];
    const probUnderage = 1 - probAdult;

    return {
      verified: probUnderage < riskTolerance,
      confidence: probAdult,
      probUnderage: probUnderage,
      minAge: minAge,
      allProbabilities,
    };
  }

  /**
   * Verify age from video element with landmarks (uses face cropping)
   */
  async verifyFromVideo(
    video: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number }>,
    minAge: AgeThreshold = 18,
    riskTolerance?: number,
    padding = 0.25
  ): Promise<AgeVerificationResult> {
    const faceCanvas = this.cropFaceRegion(video, landmarks, padding);
    return this.verify(faceCanvas, minAge, riskTolerance);
  }

  /**
   * Verify age from video frame directly (NO face cropping).
   *
   * This matches Python's inference.py exactly:
   * - Takes the full frame
   * - Resizes directly to model input size (configured, default 224×224)
   * - Runs inference
   *
   * Use this when the face is already well-positioned in the frame.
   * This produces results identical to Python's inference.py.
   */
  async verifyFromVideoDirect(
    video: HTMLVideoElement,
    minAge: AgeThreshold = 18,
    riskTolerance?: number
  ): Promise<AgeVerificationResult> {
    // Capture frame from video
    const frameCanvas = document.createElement('canvas');
    frameCanvas.width = video.videoWidth;
    frameCanvas.height = video.videoHeight;
    const frameCtx = frameCanvas.getContext('2d')!;
    frameCtx.drawImage(video, 0, 0);

    // Resize directly to model input size (matching Python's simple resize)
    const inputCanvas = document.createElement('canvas');
    inputCanvas.width = this.config.inputSize;
    inputCanvas.height = this.config.inputSize;
    const ctx = inputCanvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(frameCanvas, 0, 0, this.config.inputSize, this.config.inputSize);

    return this.verify(inputCanvas, minAge, riskTolerance);
  }

  /**
   * Verify age from canvas/image directly (NO face cropping).
   *
   * This matches Python's inference.py exactly:
   * - Takes the input canvas/image
   * - Resizes directly to model input size (configured, default 224×224)
   * - Runs inference
   *
   * Use this when you have a pre-cropped face image.
   */
  async verifyDirect(
    source: HTMLCanvasElement | HTMLImageElement,
    minAge: AgeThreshold = 18,
    riskTolerance?: number
  ): Promise<AgeVerificationResult> {
    const sourceWidth = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
    const sourceHeight = source instanceof HTMLImageElement ? source.naturalHeight : source.height;

    // Resize directly to model input size (matching Python's simple resize)
    const inputCanvas = document.createElement('canvas');
    inputCanvas.width = this.config.inputSize;
    inputCanvas.height = this.config.inputSize;
    const ctx = inputCanvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      source,
      0,
      0,
      sourceWidth,
      sourceHeight,
      0,
      0,
      this.config.inputSize,
      this.config.inputSize
    );

    return this.verify(inputCanvas, minAge, riskTolerance);
  }

  /**
   * Debug verification - returns preprocessed image and detailed results.
   * Logs a clickable base64 URL to console for visual inspection.
   */
  async verifyWithDebug(
    video: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number }>,
    minAge: AgeThreshold = 18,
    riskTolerance?: number
  ): Promise<{
    result: AgeVerificationResult;
    preprocessedImageUrl: string;
    cropSize: { width: number; height: number };
  }> {
    // Get cropped face canvas (200x200 aligned, then resized to model input)
    const faceCanvas = this.cropFaceRegion(video, landmarks);

    // Convert to base64 for debugging
    const preprocessedImageUrl = faceCanvas.toDataURL('image/png');

    // Run verification
    const result = await this.verify(faceCanvas, minAge, riskTolerance);

    // Log debug info to console
    console.log('\n========== AGE VERIFICATION DEBUG ==========');
    console.log('Preprocessed face image (click to view):');
    console.log(preprocessedImageUrl);
    console.log('\nCrop dimensions:', faceCanvas.width, 'x', faceCanvas.height);
    console.log('\nInference results per threshold:');
    console.log('┌─────────────┬────────────────┬────────────────┐');
    console.log('│ Threshold   │ Prob Adult     │ Prob Underage  │');
    console.log('├─────────────┼────────────────┼────────────────┤');
    for (const threshold of AGE_THRESHOLDS) {
      const prob = result.allProbabilities[`${threshold}+`];
      const probUnderage = 1 - prob;
      const marker = threshold === minAge ? ' ◄' : '';
      console.log(
        `│ ${String(threshold).padStart(2)}+${marker.padEnd(8)} │ ${(prob * 100).toFixed(1).padStart(12)}% │ ${(probUnderage * 100).toFixed(1).padStart(12)}% │`
      );
    }
    console.log('└─────────────┴────────────────┴────────────────┘');
    console.log(
      '\nVerification result for',
      minAge,
      '+:',
      result.verified ? '✓ VERIFIED' : '✗ REJECTED'
    );
    console.log('Risk tolerance:', riskTolerance ?? this.config.defaultRiskTolerance);
    console.log('================================================\n');

    return {
      result,
      preprocessedImageUrl,
      cropSize: { width: faceCanvas.width, height: faceCanvas.height },
    };
  }

  /**
   * Run multiple verifications and return aggregated result
   */
  async verifyMultiple(
    faceCanvas: HTMLCanvasElement,
    minAge: AgeThreshold = 18,
    config: MultiVerifyConfig = {}
  ): Promise<MultiVerificationResult> {
    const {
      numEstimates = 4,
      delayBetweenMs = 600,
      confidenceThreshold = 0.5,
      onProgress,
      captureImages = true,
    } = config;

    const estimatesWithImages: AgeVerificationWithImage[] = [];

    // Always log debug flag status for troubleshooting
    console.log('[AgeVerifier] verifyMultiple called, debug =', this.config.debug);

    // Debug header
    if (this.config.debug) {
      console.log('\n========== AGE VERIFICATION DEBUG (Multi) ==========');
      console.log(`Running ${numEstimates} verifications for ${minAge}+ threshold`);
      console.log('=====================================================\n');
    }

    for (let i = 0; i < numEstimates; i++) {
      onProgress?.(i + 1, numEstimates);

      const result = await this.verify(faceCanvas, minAge);

      let imageDataUrl: string | null = null;
      if (captureImages || this.config.debug) {
        imageDataUrl = faceCanvas.toDataURL('image/jpeg', 0.85);
      }

      // Debug log for each verification
      if (this.config.debug) {
        this.logVerificationDebug(i + 1, numEstimates, result, imageDataUrl);
      }

      // Filter by confidence threshold
      if (result.confidence >= confidenceThreshold) {
        estimatesWithImages.push({ result, imageDataUrl });
      }

      if (i < numEstimates - 1) {
        await new Promise((r) => setTimeout(r, delayBetweenMs));
      }
    }

    const aggregated = this.aggregateResults(estimatesWithImages, minAge);

    // Debug summary
    if (this.config.debug) {
      console.log('\n========== AGGREGATED RESULT ==========');
      console.log(`Verified: ${aggregated.verified ? '✓ YES' : '✗ NO'}`);
      console.log(`Avg Confidence: ${(aggregated.confidence * 100).toFixed(1)}%`);
      console.log(`Avg P(Underage): ${(aggregated.probUnderage * 100).toFixed(1)}%`);
      console.log(`Estimates used: ${aggregated.estimatesUsed}/${numEstimates}`);
      console.log('=========================================\n');
    }

    return aggregated;
  }

  /**
   * Run multiple verifications from live video, capturing fresh frames each time.
   *
   * This method addresses the bug where the same image was used for all verifications.
   * Now each verification captures a fresh frame from the video with updated landmarks.
   *
   * Pass rate logic: For 18+ threshold, verification passes when >= 80% of individual
   * checks pass (each needing >= 98% confidence).
   */
  async verifyMultipleFromVideo(
    video: HTMLVideoElement,
    minAge: AgeThreshold = 18,
    config: LiveMultiVerifyConfig
  ): Promise<MultiVerificationResult> {
    const {
      numEstimates = 4,
      delayBetweenMs = 600,
      confidenceThreshold = 0.5,
      onProgress,
      captureImages = true,
      countdownSeconds = 3,
      onCountdown,
      getLandmarks,
    } = config;

    // Countdown before starting captures - gives user time to position face
    if (countdownSeconds > 0) {
      console.log(`[AgeVerifier] Countdown starting: ${countdownSeconds} seconds`);
      for (let i = countdownSeconds; i > 0; i--) {
        onCountdown?.(i);
        await new Promise((r) => setTimeout(r, 1000));
      }
      onCountdown?.(0);
      console.log('[AgeVerifier] Countdown complete, starting captures');
    }

    const estimatesWithImages: AgeVerificationWithImage[] = [];

    // Always log debug flag status for troubleshooting
    console.log('[AgeVerifier] verifyMultipleFromVideo called, debug =', this.config.debug);

    // Debug header
    if (this.config.debug) {
      console.log('\n========== AGE VERIFICATION DEBUG (Live Multi) ==========');
      console.log(`Running ${numEstimates} verifications for ${minAge}+ threshold`);
      console.log('Each capture uses fresh frame from video');
      console.log('==========================================================\n');
    }

    for (let i = 0; i < numEstimates; i++) {
      onProgress?.(i + 1, numEstimates);

      // Get fresh landmarks for this capture
      const landmarks = getLandmarks();
      if (!landmarks || landmarks.length < 468) {
        console.warn(`[AgeVerifier] No valid landmarks for capture ${i + 1}, skipping`);
        if (i < numEstimates - 1) {
          await new Promise((r) => setTimeout(r, delayBetweenMs));
        }
        continue;
      }

      // Crop fresh face from video with current landmarks
      const faceCanvas = this.cropFaceRegion(video, landmarks);

      // Run inference on this fresh capture
      const result = await this.verify(faceCanvas, minAge);

      let imageDataUrl: string | null = null;
      if (captureImages || this.config.debug) {
        imageDataUrl = faceCanvas.toDataURL('image/jpeg', 0.85);
      }

      // Debug log for each verification
      if (this.config.debug) {
        this.logVerificationDebug(i + 1, numEstimates, result, imageDataUrl);
      }

      // Filter by confidence threshold
      if (result.confidence >= confidenceThreshold) {
        estimatesWithImages.push({ result, imageDataUrl });
      }

      if (i < numEstimates - 1) {
        await new Promise((r) => setTimeout(r, delayBetweenMs));
      }
    }

    // Require at least 2 valid face captures to proceed
    const MIN_VALID_CAPTURES = 2;
    if (estimatesWithImages.length < MIN_VALID_CAPTURES) {
      console.warn(
        `[AgeVerifier] Only ${estimatesWithImages.length}/${numEstimates} valid faces (need ${MIN_VALID_CAPTURES})`
      );
      return {
        verified: false,
        confidence: 0,
        probUnderage: 1,
        minAge,
        allProbabilities: Object.fromEntries(AGE_THRESHOLDS.map((t) => [`${t}+`, 0])),
        bestImage: null,
        estimatesUsed: estimatesWithImages.length,
        passedCount: 0,
        passRate: 0,
        noFaceDetected: true,
      };
    }

    // Use pass-rate aggregation: 80% must pass with >= 98% confidence
    const aggregated = this.aggregateResultsWithPassRate(estimatesWithImages, minAge, numEstimates);

    // Debug summary
    if (this.config.debug) {
      console.log('\n========== AGGREGATED RESULT (Pass Rate) ==========');
      console.log(`Verified: ${aggregated.verified ? '✓ YES' : '✗ NO'}`);
      console.log(`Pass Rate: ${aggregated.passRate?.toFixed(1)}%`);
      console.log(`Passed: ${aggregated.passedCount}/${aggregated.estimatesUsed}`);
      console.log(`Avg Confidence: ${(aggregated.confidence * 100).toFixed(1)}%`);
      console.log('====================================================\n');
    }

    return aggregated;
  }

  /**
   * Log debug info for a single verification
   */
  private logVerificationDebug(
    current: number,
    total: number,
    result: AgeVerificationResult,
    imageDataUrl: string | null
  ): void {
    console.log(`\n--- Verification ${current}/${total} ---`);
    if (imageDataUrl) {
      console.log('Preprocessed image (click to view):');
      console.log(imageDataUrl);
    }
    console.log('Results:');
    for (const threshold of AGE_THRESHOLDS) {
      const prob = result.allProbabilities[`${threshold}+`];
      const bar = '█'.repeat(Math.round(prob * 20)) + '░'.repeat(20 - Math.round(prob * 20));
      console.log(`  ${threshold}+: ${bar} ${(prob * 100).toFixed(1)}%`);
    }
    console.log(
      `  Verified: ${result.verified ? '✓' : '✗'} | Confidence: ${(result.confidence * 100).toFixed(1)}%`
    );
  }

  /**
   * Aggregate multiple verification results
   */
  private aggregateResults(
    estimates: AgeVerificationWithImage[],
    minAge: AgeThreshold
  ): MultiVerificationResult {
    if (estimates.length === 0) {
      console.warn('[AgeVerifier] No valid face captures. Returning failure.');
      return {
        verified: false,
        confidence: 0,
        probUnderage: 1,
        minAge,
        allProbabilities: Object.fromEntries(AGE_THRESHOLDS.map((t) => [`${t}+`, 0])),
        bestImage: null,
        estimatesUsed: 0,
        noFaceDetected: true,
      };
    }

    // Calculate averages
    const avgConfidence =
      estimates.reduce((sum, e) => sum + e.result.confidence, 0) / estimates.length;
    const avgProbUnderage =
      estimates.reduce((sum, e) => sum + e.result.probUnderage, 0) / estimates.length;

    // Aggregate all probabilities by averaging
    const aggregatedProbs: Record<string, number> = {};
    AGE_THRESHOLDS.forEach((threshold) => {
      const key = `${threshold}+`;
      aggregatedProbs[key] =
        estimates.reduce((sum, e) => sum + (e.result.allProbabilities[key] || 0), 0) /
        estimates.length;
    });

    // Find the estimate closest to average confidence for best image
    let closestIndex = 0;
    let closestDiff = Math.abs(estimates[0].result.confidence - avgConfidence);
    for (let i = 1; i < estimates.length; i++) {
      const diff = Math.abs(estimates[i].result.confidence - avgConfidence);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    const bestImage = estimates[closestIndex].imageDataUrl;

    return {
      verified: avgProbUnderage < this.config.defaultRiskTolerance,
      confidence: Math.round(avgConfidence * 1000) / 1000,
      probUnderage: Math.round(avgProbUnderage * 1000) / 1000,
      minAge,
      allProbabilities: aggregatedProbs,
      bestImage,
      allEstimates: estimates,
      estimatesUsed: estimates.length,
    };
  }

  /**
   * Aggregate results using pass-rate logic.
   *
   * For 18+ verification:
   * - Each individual check passes if confidence >= 0.98 (98%)
   * - Overall verification passes if >= 80% of checks pass
   *
   * This is more robust than averaging because it handles outliers better.
   */
  private aggregateResultsWithPassRate(
    estimates: AgeVerificationWithImage[],
    minAge: AgeThreshold,
    _totalAttempted: number
  ): MultiVerificationResult {
    if (estimates.length === 0) {
      console.warn('[AgeVerifier] No valid face captures. Returning failure.');
      return {
        verified: false,
        confidence: 0,
        probUnderage: 1,
        minAge,
        allProbabilities: Object.fromEntries(AGE_THRESHOLDS.map((t) => [`${t}+`, 0])),
        bestImage: null,
        estimatesUsed: 0,
        passedCount: 0,
        passRate: 0,
        noFaceDetected: true,
      };
    }

    // Threshold for individual verification pass (98% for 18+)
    // Risk tolerance of 2% means we need confidence >= 0.98
    const passThreshold = 1 - this.config.defaultRiskTolerance; // 0.98

    // Count how many individual verifications passed
    const passedEstimates = estimates.filter((e) => e.result.confidence >= passThreshold);
    const passedCount = passedEstimates.length;

    // Calculate pass rate based on valid estimates (not total attempted)
    const passRate = (passedCount / estimates.length) * 100;

    // Minimum pass rate required (80%)
    const minPassRate = 80;
    const verified = passRate >= minPassRate;

    // Calculate averages for reporting
    const avgConfidence =
      estimates.reduce((sum, e) => sum + e.result.confidence, 0) / estimates.length;
    const avgProbUnderage =
      estimates.reduce((sum, e) => sum + e.result.probUnderage, 0) / estimates.length;

    // Aggregate all probabilities by averaging
    const aggregatedProbs: Record<string, number> = {};
    AGE_THRESHOLDS.forEach((threshold) => {
      const key = `${threshold}+`;
      aggregatedProbs[key] =
        estimates.reduce((sum, e) => sum + (e.result.allProbabilities[key] || 0), 0) /
        estimates.length;
    });

    // Find the best image from passed estimates (or all if none passed)
    const imageSource = passedEstimates.length > 0 ? passedEstimates : estimates;
    let closestIndex = 0;
    let closestDiff = Math.abs(imageSource[0].result.confidence - avgConfidence);
    for (let i = 1; i < imageSource.length; i++) {
      const diff = Math.abs(imageSource[i].result.confidence - avgConfidence);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }
    const bestImage = imageSource[closestIndex].imageDataUrl;

    console.log(
      `[AgeVerifier] Pass rate: ${passedCount}/${estimates.length} = ${passRate.toFixed(1)}% (need ${minPassRate}%)`
    );

    return {
      verified,
      confidence: Math.round(avgConfidence * 1000) / 1000,
      probUnderage: Math.round(avgProbUnderage * 1000) / 1000,
      minAge,
      allProbabilities: aggregatedProbs,
      bestImage,
      allEstimates: estimates,
      estimatesUsed: estimates.length,
      passedCount,
      passRate: Math.round(passRate * 10) / 10,
    };
  }

  /**
   * Dispose resources
   */
  dispose(): void {
    this.session = null;
    this.isInitialized = false;
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create an age verifier with optional configuration.
 *
 * Uses the centralized SDK configuration by default.
 * Override with custom config for different models.
 *
 * @example
 * ```typescript
 * // Default config (EfficientNet-Lite0, 224×224)
 * const verifier = createAgeVerifier();
 *
 * // Use INT8 quantized model (smallest, best for mobile)
 * import { AGE_VERIFIER_MODELS } from '../constants';
 * const mobileVerifier = createAgeVerifier({
 *   ...AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_INT8,
 * });
 *
 * // Use legacy model (260×260)
 * const legacyVerifier = createAgeVerifier({
 *   ...AGE_VERIFIER_MODELS.EFFICIENTNET_B2_FP16,
 * });
 * ```
 */
export function createAgeVerifier(config?: Partial<LocalAgeVerifierConfig>): AgeVerifier {
  return new AgeVerifier(config);
}
